import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

export default function MonthlyCaseFile({ active, show }) {
  const [data, setData] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/masterfile`).then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => { if (active === "mcf") load(); }, [active, load]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    setReport(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/masterfile/upload`, fd);
      setReport(res.data);
      if (res.data.accepted) { toast.success(`Accepted — ${res.data.file.row_count} cases archived immutably`); load(); }
      else toast.error(`Rejected — ${res.data.errors.length} validation error(s); whole file rejected`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    }
    setBusy(false);
  };

  const downloadTemplate = async () => {
    const res = await axios.get(`${API}/masterfile/template`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(res.data);
    a.download = "ECH_monthly_master_file_template.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const cur = data?.current;

  return (
    <section className={`chapter utility${active === "mcf" ? " active" : ""}`} id="mcf">
      <div className="eyebrow">Working record · the ONE monthly upload every case view is computed from — due the 3rd, owner Jeff Webb</div>
      <h1>Monthly Case File</h1>
      <div className="pc-decision accept" data-testid="governing-principle" style={{ marginBottom: 14 }}>
        <b>Governing principle:</b> This site is ECH's SOP manual, monthly oversight system, and evidence archive. It is NOT a case-management or daily-entry system. Cases are worked in PS (intake), ShareFile (documents), and Alteva/Imagine (billing/IDR). The site receives ONE monthly master case file and computes every case view from it. If a feature requires typing per-case data into this site, the feature is wrong — flag it.
      </div>
      <p className="lede">One XLSX/CSV containing ALL cases — every status, intake through paid — exported from the working systems. Cumulative or month-delta both work; the site dedupes on Case ID. The file is validated as a whole (missing fields, unknown reason codes, stage regressions) and accepted or rejected entirely. Accepted files are archived immutably with uploader and server timestamp — the monthly evidence record feeding the read-only Case Pipeline and Timeline, the KPIs, MOCR Sections A/B, and the C-11 fee reconciliation.</p>

      <div className="formcard" data-testid="masterfile-upload-card">
        <div className="formcard-title">Upload — {data?.uploads?.length ? `${data.uploads.length} file(s) on the archive` : "no file yet"}</div>
        <div className="vr-btnrow" style={{ alignItems: "center" }}>
          <input type="file" accept=".csv,.xlsx" onChange={(e) => upload(e.target.files[0])} disabled={busy} data-testid="masterfile-input" />
          <button className="btn ghost" onClick={downloadTemplate} data-testid="masterfile-template-btn">Download column template (send to Jeff + Alteva)</button>
        </div>
        <p className="k-sub" style={{ marginTop: 8 }}>{(data?.columns || []).length} columns — includes the negotiation-window dates (remittance received / negotiation start / end) and award economics (payer offer, ECH offer, IDR entity fee, penalty interest). Eligibility reason codes accept ONLY the controlled list: {(data?.reason_codes || []).join(" · ")} — an unknown code rejects the file (this validation replaces the retired A-4 form's pick-list).</p>
      </div>

      {report && (
        <div className="formcard" data-testid="masterfile-report">
          <div className="formcard-title">Validation report — {report.accepted ? "ACCEPTED" : "REJECTED (whole file)"}</div>
          {report.errors?.length > 0 && (<div>{report.errors.map((e, i) => <p key={i} className="k-sub" style={{ color: "var(--red)", margin: "2px 0" }}>✗ {e}</p>)}</div>)}
          {report.warnings?.length > 0 && (<div>{report.warnings.map((w, i) => <p key={i} className="k-sub" style={{ color: "var(--amber)", margin: "2px 0" }}>⚠ {w} (accepted with data-quality flag)</p>)}</div>)}
          {(report.diff_report || []).map((d, i) => <p key={i} className="k-sub" style={{ color: "var(--amber)" }}>Δ {d}</p>)}
          {report.accepted && <p className="k-sub" style={{ color: "var(--teal)" }}>✓ {report.note}</p>}
        </div>
      )}

      {cur && (
        <div className="formcard" data-testid="masterfile-current">
          <div className="formcard-title">Current dataset — data as of {cur.as_of} <span className="vr-pill open">master file {fmt(cur.uploaded_at)}</span></div>
          <p className="k-sub">{cur.row_count} cases · {cur.aggregates.exception_count} exception flag(s) · {cur.aggregates.aging_flag_count} aging flag(s) · missed-window rate {cur.aggregates.missed_window_rate_pct ?? "—"}%. Views: <button className="vr-link" onClick={() => show("pipe")}>Case Pipeline</button> · <button className="vr-link" onClick={() => show("ctl")}>Case Timeline</button> · <button className="vr-link" onClick={() => show("kpi")}>KPIs</button></p>
        </div>
      )}

      <h2>Immutable archive</h2>
      <table data-testid="masterfile-archive-table"><tbody>
        <tr><th>Uploaded (server time)</th><th>File</th><th>Rows</th><th>SHA-256</th><th>Uploaded by</th><th>Warnings</th></tr>
        {(data?.uploads || []).length === 0 ? (
          <tr><td colSpan={6}>No data — upload monthly master file.</td></tr>
        ) : data.uploads.map((u) => (
          <tr key={u.id}>
            <td>{fmt(u.uploaded_at)}</td><td>{u.filename}</td><td>{u.row_count}</td>
            <td className="q" style={{ fontSize: 10 }}>{u.sha256.slice(0, 16)}…</td>
            <td>{u.uploaded_by?.name || "—"}</td><td>{(u.warnings || []).length}</td>
          </tr>
        ))}
      </tbody></table>
      <p style={{ color: "var(--mute)", fontSize: "13px" }}>Files are never edited or deleted — corrections arrive in the next month's upload. Timestamps are server-set; there is no backdating. Aging flags computed from every file: docs &gt;72h of service · handoff→submission &gt;3 weeks · allowable with no IDR entry after 25 days · IDR win unpaid after 30 days. Pre-conversion records (old pipeline/claims/A-4 entries) are preserved in the legacy archive with a count + checksum manifest, included in binder exports.</p>
    </section>
  );
}
